import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { selectToken, selectUser } from '../../redux/auth/selectors';
import { useSocketEventHandlers } from '../../hooks/useSocketEventHandlers';
import { BASE_URL_MAIN_SERVER } from '../../helpers/constants';

const WebSocketContext = createContext();

export const useWebSocket = () => useContext(WebSocketContext);

const WS_URL = BASE_URL_MAIN_SERVER;

export const WebSocketProvider = ({ children }) => {
  const token = useSelector(selectToken);
  const user = useSelector(selectUser);
  const socketRef = useRef(null);
  const intervalRef = useRef();
  const [isConnected, setConnected] = useState(false);

  // Додатково: для зберігання колбеків (опціонально)
  const callbacksRef = useRef({});
  const handlers = useSocketEventHandlers();

  const addCallback = useCallback(
    (event, callback) => {
      if (!event || typeof callback !== 'function') return;

      callbacksRef.current[event] = callback;

      // Якщо сокет вже підключений, одразу додати
      if (socketRef.current) {
        socketRef.current.on(event, callback);
      }
    },
    [callbacksRef, socketRef],
  );

  useEffect(() => {
    if (socketRef.current) {
      Object.entries(handlers).forEach(([event, handler]) => {
        socketRef.current.off(event);
        addCallback(event, handler);
      });
    }
  }, [addCallback, handlers]);

  const connectSocket = () => {
    if (!token || !user?._id) return;

    console.log('Connecting socket', {
      alreadyConnected: socketRef.current?.connected,
    });

    // Створення підключення до Socket.IO
    socketRef.current = io(WS_URL, {
      auth: { token, user },
      transports: ['websocket'],
    });

    socketRef.current.on('connect', () => {
      setConnected(true);
      console.log('✅ Socket connected');
      socketRef.current.emit('addOnlineUser', { id: user._id });
    });

    socketRef.current.on('disconnect', () => {
      setConnected(false);
      console.log('❌ Socket disconnected');
    });

    socketRef.current.on('connect_error', err => {
      console.error('Connect error:', err);
    });

    Object.entries(handlers).forEach(([event, handler]) => {
      socketRef.current.off(event);
      socketRef.current.on(event, handler);
    });

    // Обробка всіх callback'ів, зареєстрованих через addCallback
    Object.keys(callbacksRef.current).forEach(event => {
      socketRef.current.off(event);
      socketRef.current.on(event, callbacksRef.current[event]);
    });
  };

  const disconnectSocket = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  };

  const sendData = (data, event) => {
    if (!socketRef.current?.connected) return;
    console.log(event);

    return socketRef.current.emit(event, data);
  };

  useEffect(() => {
    connectSocket();
    return () => {
      disconnectSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?._id]);

  useEffect(() => {
    console.log(socketRef.current?.connected);

    if (socketRef.current?.connected) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        console.log('pingConnection');

        socketRef.current?.connected &&
          socketRef.current.emit('pingConnection', {});
      }, 1000 * 60 * 2);
    } else {
      clearInterval(intervalRef.current);
    }
  }, [socketRef.current?.connected]);

  const contextValue = {
    socket: socketRef.current,
    sendData,
    addCallback,
    isConnected,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;
